import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { ModernButton } from '../ui/ModernButton';
import { JobCard } from '../jobs/JobCard';
import { JobFilters } from '../jobs/JobFilters';
import { AddJobsModal } from '../jobs/AddJobsModal';
import { AssessmentModal } from '../jobs/AssessmentModal';
import { ChannelTextsModal } from '../jobs/ChannelTextsModal';
import { JobFilesModal } from '../jobs/JobFilesModal';
import { DeactivateJobModal } from '../jobs/DeactivateJobModal';
import jobListingsService from '../../services/JobListingsService';
import assessmentService, { inviteService } from '../../services/AssessmentService';
import jobDocumentService from '../../services/JobDocumentService';
import { buildAllJobsZip } from '../../services/jobPackageService';
import { downloadFile } from '../../utils/jobPackage';

const PlusIcon = (
  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

function sortJobs(jobs, sort) {
  const sorted = [...jobs];
  if (sort === 'title') {
    return sorted.sort((a, b) => a.title.localeCompare(b.title, 'de'));
  }
  if (sort === 'posted') {
    return sorted.sort((a, b) => new Date(b.datePosted || 0) - new Date(a.datePosted || 0));
  }
  return sorted.sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0));
}

/**
 * Stellenanzeigen — the job listings living in the ei4heidelberg CMS.
 *
 * Import, publish and department changes happen here; editing the content stays
 * in the CMS admin, which is the single source of truth for it.
 */
export function StellenanzeigenPage() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [department, setDepartment] = useState('all');
  const [sort, setSort] = useState('recent');

  const [departments, setDepartments] = useState([]);
  /** null until checked, so the warning does not flash on every load. */
  const [canWrite, setCanWrite] = useState(null);

  /** Which jobs already carry a questionnaire, for the card's one-line hint. */
  const [jobsWithAssessment, setJobsWithAssessment] = useState(new Set());
  const [assessmentJob, setAssessmentJob] = useState(null);

  /** Which Indeed and AfA texts each job has, by job id. */
  const [channelsByJob, setChannelsByJob] = useState(new Map());
  /** Tracking fields of each job's invitations, for the card's "in Arbeit / inaktiv" line. */
  const [invitesByJob, setInvitesByJob] = useState(new Map());
  const [channelTextsJob, setChannelTextsJob] = useState(null);
  const [filesJob, setFilesJob] = useState(null);
  const [deactivateJob, setDeactivateJob] = useState(null);

  const loadJobs = useCallback(async () => {
    setLoading(true);
    const result = await jobListingsService.getJobListings();
    if (result.success) {
      setJobs(result.data);
      setError(null);
    } else {
      setJobs([]);
      setError(result.error);
    }
    setLoading(false);
  }, []);

  const loadDepartments = useCallback(async () => {
    const result = await jobListingsService.getDepartments();
    if (result.success) setDepartments(result.data);
  }, []);

  // A missing collection only costs the hint, never the page: the service
  // returns an empty set alongside its error.
  const loadAssessmentIds = useCallback(async () => {
    const result = await assessmentService.getJobIdsWithAssessment();
    setJobsWithAssessment(result.data || new Set());
  }, []);

  const loadInviteActivity = useCallback(async () => {
    const result = await inviteService.listActivityByJob();
    setInvitesByJob(result.data || new Map());
  }, []);

  const loadChannelTexts = useCallback(async () => {
    const result = await jobDocumentService.getChannelsByJobId();
    setChannelsByJob(result.data || new Map());
  }, []);

  useEffect(() => {
    loadJobs();
    loadDepartments();
    loadAssessmentIds();
    loadChannelTexts();
    loadInviteActivity();
    // Stays null when the check is unavailable, which reads as "unknown" and
    // leaves the page usable rather than locking it on a missing endpoint.
    jobListingsService.getAuthState().then((r) => {
      const state = r.data?.authenticated;
      setCanWrite(state === null || state === undefined ? null : Boolean(state));
    });
  }, [loadJobs, loadDepartments, loadAssessmentIds, loadChannelTexts, loadInviteActivity]);

  /** { done, total } while the ZIP of all jobs is being gathered, else null. */
  const [zipProgress, setZipProgress] = useState(null);

  const downloadAllJobs = useCallback(async () => {
    setZipProgress({ done: 0, total: jobs.length });
    const result = await buildAllJobsZip(jobs, setZipProgress);
    setZipProgress(null);

    if (!result.success) {
      toast.error(result.error);
      return;
    }
    const { bytes, fileCount, failed, withoutOriginals, warning } = result.data;
    const date = new Date().toISOString().slice(0, 10);
    downloadFile(bytes, `Stellenanzeigen_${date}.zip`, 'application/zip');
    toast.success(`${jobs.length} Stellen · ${fileCount} Dateien heruntergeladen`);

    if (warning) toast.error(warning);
    if (failed.length > 0) {
      toast.error(`${failed.length} Dateien konnten nicht geladen werden, z. B. ${failed[0].filename}`);
    }
    if (withoutOriginals > 0) {
      toast(`${withoutOriginals} Stellen ohne JSON und Storyboard. Die kommen mit dem nächsten ZIP-Import.`, {
        duration: 8000,
      });
    }
  }, [jobs]);

  /** Keep a department created inline in the list without a round trip. */
  const addDepartment = useCallback((created) => {
    setDepartments((prev) =>
      prev.some((d) => d.id === created.id)
        ? prev
        : [...prev, created].sort((a, b) => a.name.localeCompare(b.name, 'de'))
    );
  }, []);

  const departmentNames = useMemo(() => departments.map((d) => d.name), [departments]);

  const visibleJobs = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const filtered = jobs.filter((job) => {
      if (status !== 'all' && job.status !== status) return false;
      if (department !== 'all' && job.department !== department) return false;
      if (!needle) return true;
      return [job.title, job.role, job.slug, job.department]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(needle));
    });
    return sortJobs(filtered, sort);
  }, [jobs, search, status, department, sort]);

  const draftIds = useMemo(() => jobs.filter((job) => job.status === 'draft').map((job) => job.id), [jobs]);
  const draftCount = draftIds.length;
  const draftLabel = draftCount === 1 ? '1 Entwurf' : `${draftCount} Entwürfe`;
  const [publishingAll, setPublishingAll] = useState(false);

  const publishAllDrafts = useCallback(async () => {
    if (!window.confirm(`${draftLabel} veröffentlichen? Sie sind danach sofort auf der Website sichtbar.`)) return;

    setPublishingAll(true);
    const result = await jobListingsService.publishJobs(draftIds);
    setPublishingAll(false);

    if (!result.success) {
      toast.error(result.error);
      return;
    }
    const { published, failed } = result.data;
    if (published > 0) toast.success(`${published} veröffentlicht`);
    if (failed.length > 0) {
      const first = jobs.find((job) => String(job.id) === String(failed[0].id));
      toast.error(`${failed.length} nicht veröffentlicht. ${first?.title ?? failed[0].id}: ${failed[0].message}`);
    }
    loadJobs();
  }, [draftLabel, draftIds, jobs, loadJobs]);

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">Stellenanzeigen</h1>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
            Alle Kampagnenseiten aus dem ei4-CMS. Bearbeitet wird im CMS.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <ModernButton variant="secondary" onClick={loadJobs} disabled={loading}>
            Aktualisieren
          </ModernButton>
          {jobs.length > 0 && (
            <ModernButton variant="secondary" onClick={downloadAllJobs} disabled={loading || zipProgress !== null}>
              {zipProgress ? `Wird gepackt … ${zipProgress.done}/${zipProgress.total}` : 'Export als ZIP'}
            </ModernButton>
          )}
          {canWrite !== false && draftCount > 0 && (
            <ModernButton variant="secondary" onClick={publishAllDrafts} disabled={loading || publishingAll}>
              {publishingAll ? 'Wird veröffentlicht …' : `${draftLabel} veröffentlichen`}
            </ModernButton>
          )}
          <ModernButton
            icon={PlusIcon}
            onClick={() => setShowAddModal(true)}
            disabled={canWrite === false}
          >
            Neue Stellen hinzufügen
          </ModernButton>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h2 className="text-lg font-semibold text-slate-700 dark:text-slate-200">
          {loading ? 'Wird geladen …' : `${visibleJobs.length} von ${jobs.length} Stellenanzeigen`}
          {/* Without a key the CMS filters drafts out, so the count would read 0
              and mean "none visible", not "none exist". */}
          {!loading && canWrite !== false && draftCount > 0 && (
            <span className="ml-2 text-sm font-normal text-amber-600 dark:text-amber-400">
              · {draftCount} als Entwurf
            </span>
          )}
          {!loading && canWrite === false && (
            <span className="ml-2 text-sm font-normal text-slate-500 dark:text-slate-400">
              · nur veröffentlichte
            </span>
          )}
        </h2>

        <JobFilters
          search={search}
          onSearch={setSearch}
          status={status}
          onStatus={setStatus}
          department={department}
          onDepartment={setDepartment}
          departments={departmentNames}
          sort={sort}
          onSort={setSort}
        />
      </div>

      {canWrite === false && (
        <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-4 py-2.5 text-sm text-amber-800 dark:text-amber-200 mb-6">
          Kein CMS-Schlüssel aktiv. Hochladen, Veröffentlichen und Bereiche ändern sind deaktiviert.
        </div>
      )}

      {error && (
        <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-300 mb-6">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
            <p className="mt-4 text-slate-600 dark:text-slate-400">Stellenanzeigen werden geladen …</p>
          </div>
        </div>
      ) : visibleJobs.length === 0 ? (
        <div className="text-center py-20 text-slate-500 dark:text-slate-400">
          {jobs.length === 0
            ? 'Noch keine Stellenanzeigen im CMS.'
            : 'Keine Stellenanzeige passt zu den Filtern.'}
        </div>
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
          {visibleJobs.map((job) => (
            <JobCard
              key={job.id}
              job={job}
              departments={departments}
              canEdit={canWrite !== false}
              hasAssessment={jobsWithAssessment.has(String(job.id))}
              channels={channelsByJob.get(String(job.id))}
              invites={invitesByJob.get(String(job.id))}
              onOpenAssessment={(selected, tab) => setAssessmentJob({ job: selected, tab })}
              onOpenChannelTexts={setChannelTextsJob}
              onOpenFiles={setFilesJob}
              onDeactivate={setDeactivateJob}
              onJobChanged={loadJobs}
              onDepartmentCreated={addDepartment}
            />
          ))}
        </div>
      )}

      {showAddModal && (
        <AddJobsModal
          onClose={() => setShowAddModal(false)}
          onImported={() => {
            loadJobs();
            loadAssessmentIds();
            loadChannelTexts();
          }}
        />
      )}

      {assessmentJob && (
        <AssessmentModal
          job={assessmentJob.job}
          initialTab={assessmentJob.tab}
          onClose={() => {
            setAssessmentJob(null);
            loadInviteActivity();
          }}
          onSaved={loadAssessmentIds}
        />
      )}

      {channelTextsJob && (
        <ChannelTextsModal
          job={channelTextsJob}
          onClose={() => setChannelTextsJob(null)}
          onSaved={loadChannelTexts}
        />
      )}

      {filesJob && <JobFilesModal job={filesJob} onClose={() => setFilesJob(null)} />}

      {deactivateJob && (
        <DeactivateJobModal job={deactivateJob} onClose={() => setDeactivateJob(null)} onDeactivated={loadJobs} />
      )}
    </div>
  );
}
